<template>
  <n-dialog
    v-model:visible="visible"
    class="enterprise-purchase-modal"
    width="560px"
    :close-on-click-modal="false"
    :destroy-on-close="false"
  >
    <ProQRCodeDialog
      v-if="payDialogShow"
      ref="proQRCodeDialogRef"
      :dialog-type="QRdialogType"
      :pay-data="QRdialogData"
      @on-close="onClose"
      @payment-result="paymentNotice = $event"
    />
    <div id="proDialog" class="proDialog">
      <header class="purchase-dialog-tabs" role="tablist">
        <div class="purchase-dialog-tabs__tab" :class="{ 'is-active': currentTab === 1 }">
          <button
            type="button"
            class="purchase-dialog-tabs__item"
            role="tab"
            :aria-selected="currentTab === 1"
            @click="setCurrentTab(1)"
          >
            {{ $t('pro_dialog_tab_title_menu') }}
          </button>
          <PurchaseTabHelpTooltip
            kind="plan"
            :global-plan="isGlobalPricing"
            :active="activePurchaseHelp === 'plan'"
            :pinned="pinnedPurchaseHelp === 'plan'"
            @open="schedulePurchaseHelp"
            @keep="keepPurchaseHelp"
            @leave="leavePurchaseHelp"
            @toggle="togglePurchaseHelp"
          />
        </div>
        <span v-if="canPurchaseFuel" class="purchase-dialog-tabs__divider" aria-hidden="true" />
        <div v-if="canPurchaseFuel" class="purchase-dialog-tabs__tab" :class="{ 'is-active': currentTab === 2 }">
          <button
            type="button"
            class="purchase-dialog-tabs__item"
            role="tab"
            :aria-selected="currentTab === 2"
            @click="setCurrentTab(2)"
          >
            {{ $t('pro_dialog_tab_title_fuel') }}
          </button>
          <PurchaseTabHelpTooltip
            kind="fuel"
            :global-plan="isGlobalPricing"
            :active="activePurchaseHelp === 'fuel'"
            :pinned="pinnedPurchaseHelp === 'fuel'"
            @open="schedulePurchaseHelp"
            @keep="keepPurchaseHelp"
            @leave="leavePurchaseHelp"
            @toggle="togglePurchaseHelp"
          />
        </div>
        <button type="button" class="purchase-dialog-tabs__close" :aria-label="t('enterprise_ui_purchase_close')" @click="handleCancel">
          <EnterpriseIcon :src="closeIcon" />
        </button>
      </header>

      <main class="purchase-dialog-content">
        <p v-if="paymentNotice" class="purchase-payment-notice" role="alert">{{ paymentNotice }}</p>
        <div class="purchase-dialog-section-head">
          <span>{{ currentTab === 1 ? $t('pro_dialog_choose_menu_title') : t('enterprise_ui_purchase_choose_fuel') }}</span>
          <el-dropdown
            trigger="click"
            class="purchase-currency-dropdown"
            :popper-options="popperOptions"
            placement="bottom-end"
            @command="onCurrencyChange"
          >
            <button type="button" class="purchase-currency-chip">
              {{ currencyLabel }}
              <EnterpriseIcon :src="chevronDownIcon" />
            </button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item v-for="code in availableCurrencyCodes" :key="code" :command="code">
                  {{ getCurrencyLabel(code) }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>

        <template v-if="currentTab === 1">
          <ProMenuItem
            ref="proMenuItemRef"
            :coin="currencyValue"
            :pricing-mode="pricingMode"
            @contact="openContactEmail"
            @selection-change="selectedPlan = $event"
          />

          <div v-if="globalPricingOnly" class="purchase-region-locked">
            <EnterpriseIcon :src="lockIcon" />
            <span>{{ t('enterprise_ui_purchase_region_locked') }}</span>
          </div>
          <button v-else type="button" class="purchase-cross-region" @click="togglePricingMode">
            <EnterpriseIcon :src="arrowLeftRightIcon" />
            <span>{{ crossRegionMessage }}</span>
            <b>{{ t(isGlobalPricing ? 'enterprise_ui_purchase_switch_local' : 'enterprise_ui_purchase_switch_global') }}</b>
            <EnterpriseIcon :src="arrowRightIcon" />
          </button>

          <button type="button" class="purchase-benefit-link" @click="showRights">
            {{ t('enterprise_ui_purchase_view_benefits') }}
            <EnterpriseIcon :src="externalLinkIcon" />
          </button>

        </template>

        <template v-else>
          <ProFuelItem
            ref="proFuelItemRef"
            :coin="currencyValue"
            :pricing-mode="fuelPricingMode"
          />
          <div class="purchase-dialog-note" :class="{ 'is-highlight': fuelPricingMode === 'global' }">
            <EnterpriseIcon v-if="fuelPricingMode === 'global'" :src="infoIcon" />
            <span>{{ fuelPurchaseNote }}</span>
          </div>
        </template>

        <div class="purchase-dialog-section-head purchase-dialog-section-head--payment">
          <span>{{ $t('pro_dialog_choose_pay_mode_title') }}</span>
        </div>

        <div class="purchase-payment-list" role="radiogroup" :aria-label="t('pro_dialog_choose_pay_mode_title')">
          <button
            v-if="isPaymentCodeAvailable(6)"
            type="button"
            class="purchase-payment-option"
            :class="{ 'is-selected': payMode === 6 }"
            role="radio"
            :aria-checked="payMode === 6"
            @click="changePayMode(6)"
          >
            <EnterpriseIcon :src="payMode === 6 ? circleDotIcon : circleIcon" class="purchase-payment-option__radio" />
            <span class="purchase-payment-option__brand is-wechat"><EnterpriseIcon :src="messageCircleIcon" /></span>
            <strong>{{ t('enterprise_ui_purchase_wechat') }}</strong>
          </button>

          <button
            v-if="isPaymentCodeAvailable(5)"
            type="button"
            class="purchase-payment-option"
            :class="{ 'is-selected': payMode === 5 }"
            role="radio"
            :aria-checked="payMode === 5"
            @click="changePayMode(5)"
          >
            <EnterpriseIcon :src="payMode === 5 ? circleDotIcon : circleIcon" class="purchase-payment-option__radio" />
            <span class="purchase-payment-option__brand is-alipay"><EnterpriseIcon :src="smartphoneIcon" /></span>
            <strong>{{ $t('pro_pay_alipay') }}</strong>
          </button>

          <button
            v-if="isPaymentCodeAvailable(1)"
            type="button"
            class="purchase-payment-option"
            :class="{ 'is-selected': payMode === 1 }"
            role="radio"
            :aria-checked="payMode === 1"
            @click="changePayMode(1)"
          >
            <EnterpriseIcon :src="payMode === 1 ? circleDotIcon : circleIcon" class="purchase-payment-option__radio" />
            <span class="purchase-payment-option__brand is-stripe"><EnterpriseIcon :src="creditCardIcon" /></span>
            <strong>{{ t('enterprise_ui_purchase_stripe_card') }}</strong>
            <small>{{ t('enterprise_ui_purchase_stripe_invoice') }}</small>
          </button>

          <button
            v-if="isPaymentCodeAvailable(4)"
            type="button"
            class="purchase-payment-option"
            :class="{ 'is-selected': payMode === 4 }"
            role="radio"
            :aria-checked="payMode === 4"
            @click="changePayMode(4)"
          >
            <EnterpriseIcon :src="payMode === 4 ? circleDotIcon : circleIcon" class="purchase-payment-option__radio" />
            <span class="purchase-payment-option__brand is-paypal"><EnterpriseIcon :src="creditCardIcon" /></span>
            <strong>PayPal</strong>
          </button>

          <button v-if="isVipSite" type="button" class="purchase-payment-option" @click="handleBankTransfer">
            <EnterpriseIcon :src="circleIcon" class="purchase-payment-option__radio" />
            <span class="purchase-payment-option__brand is-bank"><EnterpriseIcon :src="landmarkIcon" /></span>
            <strong>{{ t('enterprise_ui_purchase_bank_transfer') }}</strong>
            <small>{{ t('enterprise_ui_purchase_vat_invoice') }}</small>
          </button>
        </div>

        <button
          type="button"
          class="purchase-dialog-submit"
          :disabled="purchaseCannotSubmit"
          @click="onPay"
        >
          {{ isSubmitting ? t('enterprise_management_processing') : $t('pro_dialog_pay_btn_title') }}
        </button>
      </main>
    </div>
  </n-dialog>

  <CreateDialog ref="createDialogRef" />
  <LocalEntitlementConfirmDialog ref="localEntitlementConfirmDialogRef" />
</template>

<script setup lang="ts">
import { ElLoading } from 'element-plus'
import EnterpriseIcon from '~/components/Index/enterprise/EnterpriseIcon.vue'
import ProQRCodeDialog from '~/components/Pro/ProDialog/ProQRCodeDialog.vue'
import ProMenuItem from '~/components/Pro/ProDialog/ProMenuItem.vue'
import ProFuelItem from '~/components/Pro/ProDialog/ProFuelItem.vue'
import PurchaseTabHelpTooltip from '~/components/Pro/ProDialog/PurchaseTabHelpTooltip.vue'
import CreateDialog from '~/components/Translate/Dialog/CreateDialog.vue'
import LocalEntitlementConfirmDialog from '~/components/GlobalBiz/LocalEntitlementConfirmDialog.vue'
import arrowLeftRightIcon from '~/assets/images/enterprise/icons/arrow-left-right.svg'
import arrowRightIcon from '~/assets/images/enterprise/icons/arrow-right.svg'
import chevronDownIcon from '~/assets/images/enterprise/icons/chevron-down.svg'
import circleDotIcon from '~/assets/images/enterprise/icons/circle-dot.svg'
import circleIcon from '~/assets/images/enterprise/icons/circle.svg'
import closeIcon from '~/assets/images/enterprise/icons/x.svg'
import creditCardIcon from '~/assets/images/enterprise/icons/credit-card.svg'
import externalLinkIcon from '~/assets/images/enterprise/icons/external-link.svg'
import infoIcon from '~/assets/images/enterprise/icons/info.svg'
import landmarkIcon from '~/assets/images/enterprise/icons/landmark.svg'
import lockIcon from '~/assets/images/enterprise/icons/lock.svg'
import messageCircleIcon from '~/assets/images/enterprise/icons/message-circle.svg'
import smartphoneIcon from '~/assets/images/enterprise/icons/smartphone.svg'
import ProServer from '~/services/ProServer'
import GlobalBizServer from '~/services/GlobalBizServer'
import Toast from '~/utils/toast'
import useUserStore from '~/store/modules/user'
import useProStore from '~/store/modules/pro'
import {
  getDefaultPaymentMethod,
  getPaymentCodeByMethod,
  getPaymentMethodByCode,
} from '~/config/siteProfiles'
import { useSiteConfig } from '~/config/useSiteConfig'
import {
  getEnterpriseFuelMode,
  getEnterpriseTradeType,
  getCurrentEnterprisePlan,
  getEnterprisePlanPurchaseDecision,
  getEnterprisePurchaseError,
  isLegacyCrossRegionEnterprise,
} from '~/utils/enterpriseTrade'
import type { EnterprisePlanBlockReason } from '~/utils/enterpriseTrade'
import { isGlobalBizTradeType, rememberGlobalBizOrder } from '~/utils/globalBizOpening'
import { rememberEnterpriseCheckout } from '~/utils/enterprisePayment'
import type { EnterprisePricingMode } from '~/config/enterpriseLanding'
import type { SubscribeConfigItem } from '~/types/pro'
import type { GlobalBizEligibility } from '~/services/GlobalBizServer/types'

const { t } = useI18n()
const route = useRoute()
const userStore = useUserStore()
const proStore = useProStore()
const { userInfo, token, bizTeamInfo } = storeToRefs(userStore)
const { currentCoin, proConfig, globalProConfig, proFuelBagConfig, globalProFuelBagConfig } = storeToRefs(proStore)
const { paymentMethods, isVipSite } = useSiteConfig()
const { linkUrl, contactEmail } = useAppDomainConfig()
const { $loginCom, $StripeUtils } = useNuxtApp()

const visible = ref(false)
const currentTab = ref<1 | 2>(1)
const pricingMode = ref<EnterprisePricingMode>('local')
const initProConfig = ref<Record<string, any>>({})
const proMenuItemRef = ref<InstanceType<typeof ProMenuItem>>()
const proFuelItemRef = ref<InstanceType<typeof ProFuelItem>>()
const createDialogRef = ref<InstanceType<typeof CreateDialog>>()
const localEntitlementConfirmDialogRef = ref<InstanceType<typeof LocalEntitlementConfirmDialog>>()
const proQRCodeDialogRef = ref<InstanceType<typeof ProQRCodeDialog>>()
const payDialogShow = ref(false)
const QRdialogType = ref(5)
const QRdialogData = ref<Record<string, any>>({})
const currencyValue = ref('CNY')
const payMode = ref(isVipSite.value ? 6 : 1)
const isSubmitting = ref(false)
const paymentNotice = ref('')
const selectedPlan = ref<SubscribeConfigItem>()
type PurchaseHelpType = 'plan' | 'fuel'
const activePurchaseHelp = ref<PurchaseHelpType | null>(null)
const pinnedPurchaseHelp = ref<PurchaseHelpType | null>(null)
let purchaseHelpTimer: ReturnType<typeof setTimeout> | null = null

const isGlobalPricing = computed(() => pricingMode.value === 'global')
const enterpriseFuelMode = computed(() => getEnterpriseFuelMode(bizTeamInfo.value))
const canPurchaseFuel = computed(() => enterpriseFuelMode.value !== 'none')
const fuelPricingMode = computed<EnterprisePricingMode>(() => enterpriseFuelMode.value === 'global' ? 'global' : 'local')
const memberCannotPurchase = computed(() => Number(userInfo.value?.bizTeamMemberRole || 0) > 1)
const globalPricingOnly = computed(() => enterpriseFuelMode.value === 'global' || isLegacyCrossRegionEnterprise(bizTeamInfo.value))
const selectedPlanDecision = computed(() => getEnterprisePlanPurchaseDecision(bizTeamInfo.value, selectedPlan.value?.choiceId))
const purchaseCannotSubmit = computed(() => (
  isSubmitting.value || Boolean(paymentNotice.value) || memberCannotPurchase.value || (currentTab.value === 1 && !selectedPlanDecision.value.allowed)
))
const currencyLabel = computed(() => getCurrencyLabel(currencyValue.value))
const crossRegionMessage = computed(() => isGlobalPricing.value
  ? t('enterprise_ui_purchase_global_display')
  : t('enterprise_ui_purchase_global_prompt'))
const fuelPurchaseNote = computed(() => {
  if (fuelPricingMode.value === 'global') {
    return t('enterprise_ui_purchase_global_fuel_note')
  }
  if (!isVipSite.value) {
    return t('enterprise_ui_purchase_international_invoice_note')
  }
  return t('enterprise_ui_purchase_local_fuel_note')
})
const availableCurrencyCodes = computed(() => {
  const source = currentTab.value === 2
    ? (fuelPricingMode.value === 'global' ? globalProFuelBagConfig.value : proFuelBagConfig.value)
    : (isGlobalPricing.value ? globalProConfig.value : proConfig.value)
  const codes = Array.from(new Set(source.map(item => String(item.currency || '').toUpperCase()).filter(Boolean)))
  return codes.length ? codes : [currencyValue.value]
})

const popperOptions = {
  modifiers: [{ name: 'offset', options: { offset: [0, 6] } }],
}

const currentPaymentMethod = computed(() => getPaymentMethodByCode(payMode.value))

watch(paymentMethods, normalizePaymentMode, { immediate: true })
watch(currencyValue, normalizePaymentMode)
watch(canPurchaseFuel, (available) => {
  if (!available && currentTab.value === 2) setCurrentTab(1)
})
watch(globalPricingOnly, (active) => {
  if (active) pricingMode.value = 'global'
})
watch(availableCurrencyCodes, (codes) => {
  if (!codes.includes(currencyValue.value)) onCurrencyChange(codes[0])
})

function getCurrencyLabel(code: string) {
  const keyMap: Record<string, string> = {
    CNY: 'pro_currency_cny',
    USD: 'pro_currency_usd',
    JPY: 'pro_currency_jpy',
    HKD: 'pro_currency_hkd',
    TWD: 'pro_currency_twd',
    EUR: 'pro_currency_eur',
    GBP: 'pro_currency_gbp',
    KRW: 'pro_currency_krw',
  }
  const label = keyMap[code] ? t(keyMap[code]) : code
  return label.replace('CNY￥', 'CNY ¥').replace('USD$', 'USD $')
}

function normalizePaymentMode() {
  const nextMethod = getDefaultPaymentMethod(currentPaymentMethod.value, paymentMethods.value, currencyValue.value)
  payMode.value = getPaymentCodeByMethod(nextMethod)
}

function isPaymentCodeAvailable(code: number) {
  const method = getPaymentMethodByCode(code)
  return method ? getDefaultPaymentMethod(method, paymentMethods.value, currencyValue.value) === method : false
}

function onCurrencyChange(code: string) {
  if (!code) return
  currencyValue.value = String(code).toUpperCase()
  normalizePaymentMode()
}

function changePayMode(mode: number) {
  const method = getPaymentMethodByCode(mode)
  if (!method || getDefaultPaymentMethod(method, paymentMethods.value, currencyValue.value) !== method) {
    normalizePaymentMode()
    return
  }
  payMode.value = mode
}

function clearPurchaseHelpTimer() {
  if (!purchaseHelpTimer) return
  clearTimeout(purchaseHelpTimer)
  purchaseHelpTimer = null
}

function addOutsidePurchaseHelpListeners() {
  if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') return
  document.addEventListener('pointerdown', onOutsidePurchaseHelpPointerDown, true)
  document.addEventListener('keydown', onPurchaseHelpKeyDown)
}

function removeOutsidePurchaseHelpListeners() {
  if (typeof document === 'undefined' || typeof document.removeEventListener !== 'function') return
  document.removeEventListener('pointerdown', onOutsidePurchaseHelpPointerDown, true)
  document.removeEventListener('keydown', onPurchaseHelpKeyDown)
}

function closePurchaseHelp() {
  clearPurchaseHelpTimer()
  activePurchaseHelp.value = null
  pinnedPurchaseHelp.value = null
  removeOutsidePurchaseHelpListeners()
}

function onOutsidePurchaseHelpPointerDown(event: PointerEvent) {
  const target = event.target as HTMLElement | null
  if (target?.closest?.('[data-purchase-help]')) return
  closePurchaseHelp()
}

function onPurchaseHelpKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape') closePurchaseHelp()
}

function activatePurchaseHelp(type: PurchaseHelpType) {
  activePurchaseHelp.value = type
  addOutsidePurchaseHelpListeners()
}

function schedulePurchaseHelp(type: PurchaseHelpType) {
  clearPurchaseHelpTimer()
  if (pinnedPurchaseHelp.value) return
  purchaseHelpTimer = setTimeout(() => activatePurchaseHelp(type), 300)
}

function keepPurchaseHelp(type: PurchaseHelpType) {
  clearPurchaseHelpTimer()
  if (pinnedPurchaseHelp.value && pinnedPurchaseHelp.value !== type) return
  activatePurchaseHelp(type)
}

function leavePurchaseHelp(type: PurchaseHelpType) {
  clearPurchaseHelpTimer()
  if (pinnedPurchaseHelp.value === type) return
  purchaseHelpTimer = setTimeout(() => {
    if (activePurchaseHelp.value === type) closePurchaseHelp()
  }, 100)
}

function togglePurchaseHelp(type: PurchaseHelpType) {
  clearPurchaseHelpTimer()
  if (pinnedPurchaseHelp.value === type) {
    closePurchaseHelp()
    return
  }
  pinnedPurchaseHelp.value = type
  activatePurchaseHelp(type)
}

function setCurrentTab(tab: 1 | 2) {
  if (tab === 2 && !canPurchaseFuel.value) return
  closePurchaseHelp()
  currentTab.value = tab
  nextTick(updateInfo)
}

function togglePricingMode() {
  if (globalPricingOnly.value) return
  selectedPlan.value = undefined
  pricingMode.value = isGlobalPricing.value ? 'local' : 'global'
  initProConfig.value = { ...initProConfig.value, pricingMode: pricingMode.value }
  nextTick(() => proMenuItemRef.value?.resetSelection())
}

function showRights() {
  const enterpriseWebsiteUrl = new URL(linkUrl.value)
  enterpriseWebsiteUrl.hash = 'rights-view'
  window.open(enterpriseWebsiteUrl.toString(), '_blank', 'noopener,noreferrer')
}

function openContactEmail() {
  window.location.href = `mailto:${contactEmail.value}`
}

function openSupport() {
  if (window.$crisp?.do) window.$crisp.do('chat:open')
  else window.$crisp?.push?.(['do', 'chat:open'])
}

function handleBankTransfer() {
  Toast(t('enterprise_ui_purchase_bank_support'))
  openSupport()
}

function handleCancel() {
  closePurchaseHelp()
  visible.value = false
}

function open(item: Record<string, any>, initCoin?: string) {
  closePurchaseHelp()
  paymentNotice.value = ''
  initProConfig.value = item || {}
  pricingMode.value = globalPricingOnly.value || item?.pricingMode === 'global' ? 'global' : 'local'
  currentTab.value = item?.mode === 1 && canPurchaseFuel.value ? 2 : 1
  const targetCoin = initCoin || currentCoin.value || (isVipSite.value ? 'CNY' : 'USD')
  onCurrencyChange(targetCoin)
  visible.value = true
  nextTick(updateInfo)
}

onBeforeUnmount(closePurchaseHelp)

function updateInfo() {
  if (currentTab.value === 1) proMenuItemRef.value?.setDefaultItem(initProConfig.value)
  else proFuelItemRef.value?.setDefaultItem(initProConfig.value)
}

function onClose() {
  payDialogShow.value = false
}

function showLogin() {
  $loginCom.open()
}

async function onPay() {
  if (isSubmitting.value || paymentNotice.value) return
  isSubmitting.value = true
  try {
    await submitPurchase()
  } catch (error: any) {
    Toast(error?.message || t('enterprise_ui_purchase_request_failed'))
  } finally {
    isSubmitting.value = false
  }
}

async function submitPurchase() {
  if (!token.value) {
    showLogin()
    return
  }
  if (!bizTeamInfo.value?.id) {
    createDialogRef.value?.open(0)
    return
  }
  if (memberCannotPurchase.value) {
    Toast(t('enterprise_ui_purchase_creator_only'))
    return
  }
  if (currentTab.value === 2 && !canPurchaseFuel.value) {
    Toast(t('enterprise_ui_purchase_fuel_requires_plan'))
    setCurrentTab(1)
    return
  }
  if (currentTab.value === 2 && enterpriseFuelMode.value === 'global') {
    const currentSite = isVipSite.value ? 'VIP' : 'AI'
    const ownerSite = String(bizTeamInfo.value?.globalOwnerSite || '').toUpperCase()
    if (ownerSite && ownerSite !== currentSite) {
      Toast(t(ownerSite === 'VIP'
        ? 'enterprise_ui_purchase_fuel_mainland_site'
        : 'enterprise_ui_purchase_fuel_international_site'))
      return
    }
  }
  const planItem = currentTab.value === 1 ? proMenuItemRef.value?.getPayConfigItem() : undefined
  const currentPayItem = currentTab.value === 1 ? planItem : proFuelItemRef.value?.getPayConfigItem()
  if (!currentPayItem?.choiceId) {
    Toast(t(currentTab.value === 1 ? 'enterprise_ui_purchase_select_plan' : 'enterprise_ui_purchase_choose_fuel'))
    return
  }
  if (currentTab.value === 1) {
    const decision = getEnterprisePlanPurchaseDecision(bizTeamInfo.value, currentPayItem.choiceId)
    if (!decision.allowed) {
      Toast(getLocalizedPlanBlockMessage(decision.reason))
      return
    }
    if (planItem && decision.action === 'upgrade' && !isGlobalPricing.value && !await confirmPlanUpgrade(planItem)) return
  }

  normalizePaymentMode()
  const param: Record<string, any> = {
    coinType: currencyValue.value,
    payType: payMode.value,
    payAmount: bizTeamInfo.value.agentUid ? currentPayItem.bizAgentRealPrice : currentPayItem.realPrice,
    priceChoiceId: currentPayItem.choiceId,
    tradeType: getEnterpriseTradeType(currentPayItem.choiceId),
  }
  if (param.tradeType === null) {
    Toast(t('enterprise_ui_purchase_price_config_error'))
    return
  }
  if (planItem && !await prepareGlobalBizPurchase(param, planItem)) return

  param.continueSub = 0
  param.payReturnUrl = `${window.location.origin}/account/pay/payResult`
  param.payCancelUrl = `${window.location.origin}/account/pay/payCancel`
  saveRouter()

  const listDom = document.getElementById('proDialog')
  const loading = listDom ? ElLoading.service({ lock: true, target: listDom }) : undefined
  if (payMode.value === 1) {
    try {
      await $StripeUtils.openStripeCheckOut(param, (paymentIntent: string) => {
        if (paymentIntent) Toast(paymentIntent)
      })
    } finally {
      loading?.close()
    }
    return
  }

  await ProServer.getRequestBuyTranslateTrade(param).then((data) => {
    loading?.close()
    if (data.code !== '1') {
      Toast(getLocalizedPurchaseError(data.code, data.message))
      return
    }
    if (isGlobalBizTradeType(param.tradeType)) rememberGlobalBizOrder(data.data?.orderNo)
    if (payMode.value === 5 || payMode.value === 6) {
      if (isMobileOrTablet()) {
        if (/Quark/.test(navigator.userAgent)) window.location.href = data.data.alipayHrefUrl
        else {
          const link = document.createElement('a')
          link.href = data.data.alipayHrefUrl
          link.target = '_blank'
          link.style.display = 'none'
          link.click()
        }
      } else {
        const url = payMode.value === 5 ? data.data.alipayHrefUrl : data.data.wechatHrefUrl
        nextTick(() => {
          QRdialogType.value = payMode.value
          QRdialogData.value = { url, price: param.payAmount, orderNo: data.data.orderNo }
          payDialogShow.value = true
        })
      }
      return
    }
    if (data.data.paypalHrefUrl) {
      const paymentUrl = new URL(data.data.paypalHrefUrl)
      const paymentSession = paymentUrl.searchParams.get('token') || paymentUrl.searchParams.get('ba_token')
      if (paymentSession) rememberEnterpriseCheckout(paymentSession, data.data.orderNo)
      const link = document.createElement('a')
      link.href = data.data.paypalHrefUrl
      link.style.display = 'none'
      link.click()
    }
  }).catch((error) => {
    loading?.close()
    Toast(error?.message || t('enterprise_ui_purchase_request_failed'))
  })
}

async function confirmPlanUpgrade(item: SubscribeConfigItem, eligibility?: GlobalBizEligibility): Promise<boolean> {
  return Boolean(await localEntitlementConfirmDialogRef.value?.open(eligibility || {
    eligible: true,
    site: isVipSite.value ? 'VIP' : 'AI',
    userAccount: '',
    bizTeamId: Number(bizTeamInfo.value?.id),
    localEntitlementActive: false,
    localServiceLevel: bizTeamInfo.value?.serviceLevel || 0,
    localRemainingQuota: bizTeamInfo.value?.remainServiceWords || 0,
    localEndTime: bizTeamInfo.value?.serviceEndTime || undefined,
  }, {
    targetName: isGlobalPricing.value ? t('enterprise_ui_pricing_global_plan', { name: item.title }) : item.title,
    targetGlobal: isGlobalPricing.value,
    currentPlan: getCurrentEnterprisePlan(bizTeamInfo.value),
    remainingQuota: eligibility?.localRemainingQuota ?? bizTeamInfo.value?.remainServiceWords ?? 0,
    endTime: eligibility?.localEndTime ?? bizTeamInfo.value?.serviceEndTime ?? undefined,
  }))
}

async function prepareGlobalBizPurchase(param: Record<string, any>, item: SubscribeConfigItem): Promise<boolean> {
  if (!isGlobalBizTradeType(param.tradeType)) return true
  try {
    const response = await GlobalBizServer.eligibility(param.tradeType)
    if (response.code !== '1' || !response.data) {
      Toast(getLocalizedPurchaseError(response.code, response.message || t('enterprise_ui_purchase_eligibility_failed')))
      return false
    }
    if (!response.data.eligible) {
      Toast(getLocalizedPurchaseError(response.data.reasonCode))
      return false
    }
    if (!response.data.localEntitlementActive && !response.data.globalUpgrade) return true
    const confirmed = await confirmPlanUpgrade(item, response.data)
    if (!confirmed) return false
    if (response.data.localEntitlementActive) {
      param.globalLocalOverrideConfirmed = true
      param.globalLocalEntitlementSnapshotHash = response.data.localEntitlementSnapshotHash
    }
    return true
  } catch (error: any) {
    Toast(error?.message || t('enterprise_ui_purchase_eligibility_failed'))
    return false
  }
}

// 固定业务原因在展示边界翻译，保留接口返回与未知错误详情。
function getLocalizedPlanBlockMessage(reason: EnterprisePlanBlockReason): string {
  const keys: Record<EnterprisePlanBlockReason, string> = {
    'unknown-plan': 'enterprise_ui_purchase_select_plan',
    'unknown-entitlement': 'enterprise_ui_purchase_unknown_entitlement',
    'global-to-local': 'enterprise_ui_purchase_global_to_local',
    downgrade: 'enterprise_ui_purchase_downgrade',
    'current-plan': 'enterprise_ui_purchase_no_renewal',
  }
  return t(keys[reason])
}

function getLocalizedPurchaseError(code?: string, fallback?: string): string {
  const keys: Record<string, string> = {
    '10022': 'enterprise_ui_purchase_error_upgrade_rule',
    '10057': 'enterprise_ui_purchase_error_email_password',
    '10058': 'enterprise_ui_purchase_error_set_password',
    '10059': 'enterprise_ui_purchase_error_creator_link',
    '10062': 'enterprise_ui_purchase_error_owner_site',
    '10064': 'enterprise_ui_purchase_error_plan_changed',
    '10065': 'enterprise_ui_purchase_error_pending_contract',
  }
  return keys[code || '']
    ? t(keys[code || ''])
    : getEnterprisePurchaseError(code, fallback || t('enterprise_ui_purchase_error_default'))
}

function saveRouter() {
  localStorage.setItem('payRouter', JSON.stringify({ url: route.path, query: route.query }))
}

defineExpose({ open })
</script>

<style scoped lang="scss">
:global(.enterprise-purchase-modal.el-dialog) {
  width: 560px !important;
  height: max-content !important;
  min-height: 0 !important;
  max-width: calc(100vw - 32px);
  margin: 16px auto;
  padding: 0;
  overflow: hidden;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(24, 56, 96, 0.24), 0 8px 24px rgba(43, 125, 233, 0.18);
}

:global(.enterprise-purchase-modal .el-dialog__header) {
  display: none;
}

:global(.enterprise-purchase-modal .el-dialog__body) {
  height: max-content !important;
  padding: 0;
}

.proDialog {
  width: 100%;
  height: fit-content !important;
  min-height: 0 !important;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  color: #1f2329;
  background: #fff;
}

.purchase-dialog-tabs {
  position: sticky;
  top: 0;
  z-index: 5;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 0 34px;
  border-bottom: 1px solid #eef1f5;
  background: #fff;

  &__tab {
    position: relative;
    height: 100%;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0 20px;

    &::after {
      position: absolute;
      bottom: 0;
      left: 50%;
      width: 44px;
      height: 3px;
      border-radius: 2px;
      background: transparent;
      content: '';
      transform: translateX(-50%);
    }

    &.is-active::after {
      background: #2b7de9;
    }

    &.is-active .purchase-dialog-tabs__item {
      color: #2b7de9;
      font-weight: 700;
    }
  }

  &__item {
    padding: 0;
    color: #5f6875;
    font-size: 14.5px;
    border: 0;
    outline: none;
    background: transparent;
    cursor: pointer;

    &:focus-visible {
      border-radius: 3px;
      box-shadow: 0 0 0 2px rgba(43, 125, 233, .2);
    }
  }

  &__divider {
    width: 1px;
    height: 14px;
    flex: 0 0 1px;
    background: #d0d5dd;
  }

  &__close {
    position: absolute;
    top: 14px;
    right: 34px;
    width: 16px;
    height: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: #8b95a1;
    border: 0;
    background: transparent;
    cursor: pointer;

    .enterprise-svg-icon {
      width: 16px;
      height: 16px;
    }
  }
}

.purchase-dialog-content {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 26px 24px;
}

.purchase-payment-notice {
  margin: 0;
  padding: 12px 14px;
  color: #92400e;
  font-size: 12px;
  line-height: 1.6;
  border-radius: 10px;
  background: #fff7e6;
}

.purchase-dialog-section-head {
  min-height: 27px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #5f6875;
  font-size: 12.5px;

  &--payment {
    min-height: 18px;
  }
}

.purchase-currency-chip {
  height: 27px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  color: #1f2329;
  font-size: 12px;
  font-weight: 700;
  border: 0;
  border-radius: 8px;
  outline: none;
  background: #f2f4f7;
  cursor: pointer;

  .enterprise-svg-icon {
    width: 11px;
    height: 11px;
    color: #8b95a1;
  }
}

.purchase-cross-region {
  width: 100%;
  min-height: 40px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  color: #2b7de9;
  text-align: left;
  border: 1px solid #cfe2ff;
  border-radius: 10px;
  background: #eaf3ff;
  cursor: pointer;

  > .enterprise-svg-icon {
    width: 15px;
    height: 15px;
    flex: 0 0 15px;
  }

  > span {
    min-width: 0;
    flex: 1;
    font-size: 12.5px;
    font-weight: 600;
    line-height: 18px;
  }

  > b {
    flex: 0 0 auto;
    font-size: 12.5px;
    font-weight: 700;
    line-height: 18px;
    white-space: nowrap;
  }

  > .enterprise-svg-icon:last-child {
    width: 13px;
    height: 13px;
    flex-basis: 13px;
  }
}

.purchase-region-locked {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  color: #8b95a1;
  font-size: 11.5px;
  line-height: 1.5;
  border: 1px solid #e3e7ec;
  border-radius: 10px;
  background: #fafafa;

  .enterprise-svg-icon {
    width: 14px;
    height: 14px;
    flex: 0 0 14px;
    color: #c3c9d1;
  }
}

.purchase-benefit-link {
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  color: #2b7de9;
  font-size: 12px;
  font-weight: 600;
  line-height: 17px;
  border: 0;
  background: transparent;
  cursor: pointer;

  .enterprise-svg-icon {
    width: 12px;
    height: 12px;
  }
}

.purchase-dialog-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  color: #8b95a1;
  font-size: 11px;
  line-height: 1.55;

  > span {
    min-width: 0;
    flex: 1;
  }

  &.is-highlight {
    padding: 9px 12px;
    color: #2b7de9;
    font-size: 11.5px;
    font-weight: 600;
    border-radius: 10px;
    background: #eaf3ff;

    .enterprise-svg-icon {
      width: 14px;
      height: 14px;
      flex: 0 0 14px;
      margin-top: 2px;
    }
  }
}

.purchase-payment-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.purchase-payment-option {
  width: 100%;
  min-height: 38px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  color: #1f2329;
  text-align: left;
  border: 1px solid #e3e7ec;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;

  &:hover,
  &.is-selected {
    border-color: #2b7de9;
    background: #f5f9ff;
  }

  &__radio {
    width: 14px;
    height: 14px;
    flex: 0 0 14px;
    color: #c3c9d1;
  }

  &.is-selected &__radio {
    color: #2b7de9;
  }

  &__brand {
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    border-radius: 5px;

    .enterprise-svg-icon {
      width: 12px;
      height: 12px;
    }

    &.is-wechat { background: #07c160; }
    &.is-alipay { background: #1677ff; }
    &.is-stripe { background: #635bff; }
    &.is-paypal { background: #0070ba; }
    &.is-bank { background: #64748b; }
  }

  strong {
    font-size: 12.5px;
    font-weight: 600;
    line-height: 18px;
  }

  small {
    margin-left: auto;
    color: #8b95a1;
    font-size: 10.5px;
    line-height: 15px;
  }
}

.purchase-dialog-submit {
  width: 100%;
  min-height: 44px;
  color: #fff;
  font-size: 14.5px;
  font-weight: 700;
  border: 0;
  border-radius: 10px;
  background: #2b7de9;
  cursor: pointer;
  transition: background 0.18s ease;

  &:hover:not(:disabled) {
    background: #236fd1;
  }

  &:disabled {
    background: #c3c9d1;
    cursor: not-allowed;
  }
}

@media (max-width: 640px) {
  .purchase-dialog-tabs {
    padding: 0 24px;

    &__close {
      right: 18px;
    }
  }

  .purchase-dialog-content {
    padding: 14px 18px 20px;
  }

  .purchase-cross-region {
    align-items: flex-start;
    flex-wrap: wrap;

    > b {
      margin-left: 25px;
    }
  }

  .purchase-payment-option small {
    display: none;
  }
}
</style>
